import type {
  CyclePregnancyStatusRecord,
  CyclePregnancyStatusWrite,
  PatientPregnancyBirthType,
  PatientPregnancyLossType,
  PatientPregnancyStands,
  PatientPregnancyDatePrecision,
} from "@src/packages/homepage/pregnancy/useCyclePregnancyStatus";

/**
 * What a patient can say about a pregnancy that was recorded from a positive
 * result.
 *
 * Three answers, because those are the three things that can be true, and each
 * carries only optional detail. Every field below may be left out: someone
 * telling the app her pregnancy has ended must not be held at a form.
 *
 * The kinds are the API's own enums, read off the generated client, so a
 * vocabulary change breaks the build here rather than at the request.
 */
export type PregnancyStatus =
  | {
      stands: "continuing";
      /** Roughly how far along, if she knows or wants to say. */
      weekCount?: number | null;
    }
  | {
      stands: "ended";
      /**
       * Which kind of loss, *if a clinic has told her*. Optional on purpose:
       * "missed miscarriage" and "anembryonic pregnancy" are words a clinic says
       * to you, and being blocked by a list of them at that moment is the whole
       * thing this must not get wrong. Left out, the record says a pregnancy
       * ended and claims nothing more.
       */
      kind?: PatientPregnancyLossType | null;
      /** At roughly how many weeks, if she wants to say. */
      weekCount?: number | null;
      /** The day it happened, if she wants to give one. */
      eventDate?: string | null;
      eventDatePrecision?: PatientPregnancyDatePrecision;
    }
  | {
      stands: "birth";
      kind: PatientPregnancyBirthType;
      liveBirthCount?: number | null;
      /** The day the baby was born, if she wants to give one. */
      eventDate?: string | null;
      eventDatePrecision?: PatientPregnancyDatePrecision;
    };

/**
 * Why a second answer supersedes the first.
 *
 * The API requires a reason alongside `supersedesStatusId`, and this one is
 * stored as provenance on the successor row and never rendered — so it stays a
 * single stable phrase rather than a translated string that would differ per
 * locale inside a medical record. The same choice, for the same reason, as
 * `PATIENT_OUTCOME_REPEAT_CORRECTION_REASON` in `patientOutcomeWrite.ts`.
 */
export const PATIENT_PREGNANCY_STATUS_CORRECTION_REASON =
  "Patient updated where her pregnancy stands";

/**
 * Turn her answer into the request that records it.
 *
 * This writes a *new patient-owned fact*, linked to the positive outcome by
 * `outcomeId`. It does not correct that outcome, create a second one, or touch
 * it in any way — which is the whole point of #3492: the app used to state a
 * loss by rewriting the clinical record to `type: "miscarriage"`, and a positive
 * result her clinic authored was not hers to rewrite.
 *
 * A `current` record means this is a correction of her own earlier answer, and
 * the API refuses an uncorrected second version — so the caller passes what it
 * read and the supersedes pair travels with it.
 *
 * `reportedDate` is only ever set for a continuing pregnancy: it is the day she
 * said it, which is what dates a "still pregnant". A loss or a birth carries
 * `eventDate` instead — the day the thing actually happened — and the app must
 * not date one from the other.
 */
export function buildPatientPregnancyStatusWrite(input: {
  status: PregnancyStatus;
  outcomeId: string;
  clientRequestId: string;
  /** What the cycle currently holds, or null when she has not said before. */
  current: Pick<CyclePregnancyStatusRecord, "id"> | null;
  /** Today, in her calendar, for a continuing answer's reported date. */
  today: string;
}): CyclePregnancyStatusWrite {
  const { status } = input;
  const correction = input.current
    ? {
        supersedesStatusId: input.current.id,
        correctionReason: PATIENT_PREGNANCY_STATUS_CORRECTION_REASON,
      }
    : {};
  const shared = {
    outcomeId: input.outcomeId,
    clientRequestId: input.clientRequestId,
    ...correction,
  };

  if (status.stands === "continuing") {
    return {
      ...shared,
      status: "continuing",
      reportedDate: input.today,
      reportedDatePrecision: "day",
      gestationalWeek: status.weekCount ?? null,
    };
  }

  // Both dates are optional and travel with their precision, which the API
  // requires as a pair. Absent, the server dates the assertion from the write —
  // honest, and better than inventing a day she did not give.
  const event = status.eventDate
    ? {
        eventDate: status.eventDate,
        eventDatePrecision: status.eventDatePrecision ?? "day",
      }
    : {};

  if (status.stands === "ended") {
    return {
      ...shared,
      ...event,
      status: "ended",
      gestationalWeek: status.weekCount ?? null,
      lossType: status.kind ?? null,
    };
  }

  return {
    ...shared,
    ...event,
    status: "birth",
    birthType: status.kind,
    liveBirthCount: status.liveBirthCount ?? null,
  };
}

/**
 * What may be shown about a pregnancy on a cycle a partner can see.
 *
 * Partner access is all-or-nothing: there is no per-field sharing control, so
 * anything put on that card is visible to him with no way for her to withhold it.
 * That is why this returns a *summary* and not the record.
 *
 * **The kind of loss is deliberately not here.** "Ectopic pregnancy", "missed
 * miscarriage" — she entered those for her record and her clinic, and until she can
 * choose who sees them, nobody else does. What a partner gets is where things
 * stand and how far along, which is what lets him follow without being handed a
 * clinical detail about her body.
 *
 * It reads her own statement and nothing else. Not the outcome — `type:
 * "pregnancy"` is written by the beta recording, not by her, and reading it as
 * her answer put "Still pregnant" under the result of every patient with a
 * positive beta, including one who miscarried yesterday. Not the follow-up
 * either: that is tracking configuration, and #3492 retires it as the
 * pregnancy-status authority. Absent a statement this returns null and the card
 * says nothing, which stays the honest answer — the result is already stated
 * above it.
 */
export function summarisePregnancyStatus(
  record: Pick<
    CyclePregnancyStatusRecord,
    "status" | "gestationalWeek" | "liveBirthCount"
  > | null,
): {
  stands: PatientPregnancyStands;
  weekCount: number | null;
  liveBirthCount: number | null;
} | null {
  if (!record) return null;
  const born = record.status === "birth";

  return {
    stands: record.status,
    // Not on a birth. The week is preserved on the record on purpose — it is how
    // far along she was when she last said — but read out beside a birth it
    // becomes a claim about the birth: "A baby was born · At 8 weeks". A week
    // belongs to a pregnancy in progress, or to the point one ended.
    weekCount: born ? null : (record.gestationalWeek ?? null),
    liveBirthCount: born ? (record.liveBirthCount ?? null) : null,
  };
}

/**
 * Which copy a recorded ending is described in, once she has said where the
 * pregnancy stands.
 *
 * The cycle's outcome `type` says what the *result* was, and a birth or a loss
 * she reports never changes it — that is the separation #3492 exists to keep. So
 * the type alone cannot tell a birth from a beta drawn last week, and read from
 * it by itself a birth was headed "A positive result" and told her the clinic
 * would confirm what comes next.
 *
 * Kept as a function on the presenter rather than a branch inside the card so the
 * mapping can be asserted, and so both the card and anything else that describes a
 * recorded ending reach the same answer.
 */
export function recordedResultVoice<Ending extends string>(
  ending: Ending,
  stands: PatientPregnancyStands | null | undefined,
): Ending | "birth" | "miscarriage" {
  if (ending !== "pregnancy") return ending;
  // A loss no longer rewrites the outcome to `miscarriage`, so the card would
  // otherwise still be headed "A positive result" above her own words saying the
  // pregnancy ended.
  if (stands === "ended") return "miscarriage";
  return stands === "birth" ? "birth" : ending;
}
